/*
 * Instant task reporting (v1 slice) — a collapsible card next to Task
 * Generation on the sales task list.
 *
 * Two plain tables, by rule and by rep, over a date range in the header timezone. The PRD wants
 * "enough to spot a rep or a rule that isn't converting", not a dashboard, so
 * there are no charts and no trends.
 *
 * SUPER_USER only (product decision Q14). The gate is the `can_view` prop, which
 * the page passes from the SAME `can_edit` boolean the generation config GET
 * returns — one server-derived capability rather than a second client-side role
 * check that could drift from it. Like every capability boolean here it only
 * decides what renders; the endpoint enforces access itself.
 *
 * The range is the report's own, deliberately not the list's due-date filter:
 * this report counts by created/claimed date, so sharing the filter would make
 * the two disagree while looking like one control. It uses the same
 * DateRangeFilter component.
 */

import React, { Component } from 'react';
import { FaChevronDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa';
import DateRangeFilter from '../early_retention_checklist/DateRangeFilter';
import StackTd from './StackTable';
import { stackClasses } from './stack_table';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import {
  autoCompletedRate,
  defaultReportRange,
  dismissRate,
  formatRate,
  formatSeconds,
  isReportEmpty,
  normalizeReport,
  repTotals,
  reportUrlPath,
  ruleTotals
} from './instant_report';
import type { InstantReportData, ReportRange } from './instant_report';
import { filterTzAbbr, resolveFilterTz } from './filter_tz';

interface Props {
  // SUPER_USER, as reported by the generation config endpoint's can_edit.
  can_view?: boolean;
  // When set, the parent owns expansion (the page's tab) and the header is static.
  open?: boolean;
  // Header timezone (IANA) the date range is in; unset = IST, as the server reads it.
  tz?: string;
}

interface State {
  collapsed: boolean;
  loading: boolean;
  // Set on a failed fetch and cleared on the next one. Held as a message
  // rather than a boolean so the server's own reason reaches the operator.
  error: string;
  // Whether a response has landed at least once, so the empty state is not
  // shown in place of the first load.
  loaded: boolean;
  report: InstantReportData;
  dateFrom: string;
  dateTo: string;
}

interface ReportError {
  response?: { data?: { reason?: string } };
}

const EMPTY_REPORT: InstantReportData = { by_rule: [], by_rep: [] };

// The endpoint needs both bounds (an unbounded report would scan the table), so
// the range state is never empty: "clear" resets to the default window rather
// than to "any date" the way the list's own filter does.
const rangeState = (tz?: string): Pick<State, 'dateFrom' | 'dateTo'> => {
  const { from, to } = defaultReportRange(tz);
  return { dateFrom: from, dateTo: to };
};

const isCollapsed = (props: Props, state: State): boolean =>
  props.open === undefined ? state.collapsed : !props.open;

const cellClass = 'px-3 py-2 text-right tabular-nums';
const headClass = 'px-3 py-2 text-right font-medium text-content-muted';
const firstHead = 'w-[30%] px-3 py-2 text-left font-medium text-content-muted';
const ST = stackClasses();
const RULE_COLS = ['Created', 'Opened', 'Completed', 'Auto-completed', 'Median time to open'];
const REP_COLS = ['Opened', 'Completed', 'Alerts shown', 'Alerts dismissed', 'Dismiss rate'];

// Numeric cells of one row, labelled for card mode from the column list.
const numCells = (cols: string[], values: React.ReactNode[]) =>
  values.map((v, i) => (
    <StackTd key={cols[i]} label={cols[i]} className={cellClass}>
      {v}
    </StackTd>
  ));

const headRow = (first: string, cols: string[]) => (
  <thead className={ST.head}>
    <tr className="border-b border-line">
      <th className={firstHead}>{first}</th>
      {cols.map((c) => (
        <th key={c} className={headClass}>
          {c}
        </th>
      ))}
    </tr>
  </thead>
);

class InstantReport extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      collapsed: true,
      loading: false,
      error: '',
      loaded: false,
      report: EMPTY_REPORT,
      ...rangeState(props.tz)
    };
  }

  componentDidMount() {
    const { can_view: canView = false } = this.props;
    if (canView && !isCollapsed(this.props, this.state)) this.loadReport();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Fetch lazily: the report is an aggregate query and most agents never open
    // the card. First expansion loads it; after that only Refresh, a range
    // change and a header-timezone change do.
    const { loaded } = this.state;
    const { can_view: canView = false, tz } = this.props;
    const opened = !isCollapsed(this.props, this.state) && isCollapsed(prevProps, prevState);
    if (canView && opened && !loaded) {
      this.loadReport();
      return;
    }
    // Same calendar days, re-read in the new zone; only once it has been loaded.
    if (canView && loaded && prevProps.tz !== tz) this.loadReport();
  }

  setRange = ({ from, to }: ReportRange) => {
    this.setState({ dateFrom: from, dateTo: to }, this.loadReport);
  };

  resetRange = () => {
    const { tz } = this.props;
    this.setState(rangeState(tz), this.loadReport);
  };

  loadReport = () => {
    const { dateFrom, dateTo } = this.state;
    const { tz } = this.props;
    if (!dateFrom || !dateTo) return;
    this.setState({ loading: true, error: '' });
    const url = getApiUrl(reportUrlPath({ date_from: dateFrom, date_to: dateTo, tz }));
    getApi(url, true)
      .then((res: { data?: { data?: Parameters<typeof normalizeReport>[0] } }) => {
        this.setState({
          report: normalizeReport(res?.data?.data),
          loading: false,
          loaded: true
        });
      })
      .catch((e: ReportError) => {
        this.setState({
          loading: false,
          loaded: true,
          report: EMPTY_REPORT,
          error: e?.response?.data?.reason || 'Could not load the instant task report'
        });
      });
  };

  renderRuleTable = () => {
    const {
      report: { by_rule: byRule }
    } = this.state;
    const totals = ruleTotals(byRule);
    return (
      <table className={ST.table}>
        {headRow('Rule', RULE_COLS)}
        <tbody className={ST.body}>
          {byRule.map((row) => (
            <tr key={row.task_type} className={`border-t border-line ${ST.row}`}>
              <StackTd className="px-3 py-2 text-left font-medium md:font-normal">
                {row.task_label}
              </StackTd>
              {numCells(RULE_COLS, [
                row.created,
                row.opened,
                row.completed,
                row.auto_completed,
                formatSeconds(row.median_time_to_open_seconds)
              ])}
            </tr>
          ))}
        </tbody>
        <tfoot className={ST.body}>
          <tr
            className={`border-t-2 font-semibold ${ST.row}`}
            style={{ borderColor: 'var(--border-default)' }}
          >
            <StackTd className="px-3 py-2 text-left">
              All rules
              <span className="ml-2 text-xs font-normal text-content-muted">
                {formatRate(autoCompletedRate(totals))} expired unworked
              </span>
            </StackTd>
            {numCells(RULE_COLS, [
              totals.created,
              totals.opened,
              totals.completed,
              totals.auto_completed,
              '—'
            ])}
          </tr>
        </tfoot>
      </table>
    );
  };

  /*
   * The rep cut carries no Created or Auto-completed column: a task is created
   * before anyone holds it and expires without anyone holding it, so neither is
   * attributable to a rep, and the endpoint sends neither. The pop-up pair takes
   * their place — dismissed/shown is the PRD's pop-up fatigue metric.
   */
  renderRepTable = () => {
    const {
      report: { by_rep: byRep }
    } = this.state;
    const totals = repTotals(byRep);
    return (
      <table className={ST.table}>
        {headRow('Rep', REP_COLS)}
        <tbody className={ST.body}>
          {byRep.map((row) => (
            <tr key={row.ops_uuid} className={`border-t border-line ${ST.row}`}>
              <StackTd
                className="px-3 py-2 text-left capitalize truncate font-medium md:font-normal"
                title={row.name}
              >
                {row.name}
              </StackTd>
              {numCells(REP_COLS, [
                row.opened,
                row.completed,
                row.alerts_shown,
                row.alerts_dismissed,
                formatRate(dismissRate(row))
              ])}
            </tr>
          ))}
        </tbody>
        <tfoot className={ST.body}>
          <tr
            className={`border-t-2 font-semibold ${ST.row}`}
            style={{ borderColor: 'var(--border-default)' }}
          >
            <StackTd className="px-3 py-2 text-left">All reps</StackTd>
            {numCells(REP_COLS, [
              totals.opened,
              totals.completed,
              totals.alerts_shown,
              totals.alerts_dismissed,
              formatRate(dismissRate(totals))
            ])}
          </tr>
        </tfoot>
      </table>
    );
  };

  renderBody = () => {
    const { loading, error, loaded, report } = this.state;
    if (loading && !loaded) {
      return (
        <div className="px-5 py-10 text-center text-sm text-content-muted">
          Loading instant task report…
        </div>
      );
    }
    if (error) {
      return (
        <div className="px-5 py-8 text-center text-sm" style={{ color: 'var(--state-error)' }}>
          {error}
        </div>
      );
    }
    if (loaded && isReportEmpty(report)) {
      return (
        <div className="px-5 py-8 text-center text-sm text-content-muted">
          No instant tasks in this date range.
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-5 pb-4">
        <div>
          <h3 className="px-5 py-2 text-sm font-semibold">By rule</h3>
          {this.renderRuleTable()}
        </div>
        <div>
          <h3 className="px-5 py-2 text-sm font-semibold">By rep</h3>
          {this.renderRepTable()}
        </div>
      </div>
    );
  };

  render() {
    const { can_view: canView = false } = this.props;
    // Q14: SUPER_USER only. Unlike the generation card there is no read-only
    // mode — a report nobody may act on is not worth the aggregate query.
    if (!canView) return null;

    const { loading, dateFrom, dateTo } = this.state;
    const { open, tz } = this.props;
    const collapsed = isCollapsed(this.props, this.state);
    const Chevron = collapsed ? FaChevronRight : FaChevronDown;
    const title = (
      <>
        <h2 className="font-semibold">Instant task report</h2>
        <span className="text-xs text-content-muted">
          Instant tasks created, opened, completed and auto-completed, by rule and by rep.
        </span>
      </>
    );
    return (
      <section
        data-testid="instant-report"
        className="bg-surface-card rounded-lg shadow-sm border border-line"
        style={{ margin: '20px 5%' }}
      >
        {open !== undefined ? (
          <div className="w-full px-5 py-3 flex flex-wrap items-center gap-2 border-b border-line">
            {title}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => this.setState((prev) => ({ collapsed: !prev.collapsed }))}
            aria-expanded={!collapsed}
            className={`w-full px-5 py-3 flex items-center gap-2 text-left hover:bg-surface-sunken transition-colors ${
              collapsed ? '' : 'border-b border-line'
            }`}
            // Explicit colour/margin override the host CMS's global `button {}`
            // rule, which would otherwise paint white text on a white card.
            style={{
              background: 'transparent',
              border: 'none',
              margin: 0,
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
          >
            <Chevron size={12} className="text-content-muted shrink-0" />
            {title}
          </button>
        )}
        {collapsed ? null : (
          <>
            <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
              <DateRangeFilter
                from={dateFrom}
                to={dateTo}
                onChange={this.setRange}
                onClear={this.resetRange}
              />
              <button
                type="button"
                onClick={this.loadReport}
                disabled={loading}
                aria-label="Refresh instant task report"
                className="px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
                style={{
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-primary)',
                  cursor: loading ? 'wait' : 'pointer',
                  margin: 0
                }}
              >
                <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
              <span className="text-xs text-content-muted" title={resolveFilterTz(tz)}>
                Dates are {filterTzAbbr(tz)} calendar days (header timezone), like every other date
                filter on this page.
              </span>
            </div>
            {this.renderBody()}
          </>
        )}
      </section>
    );
  }
}

export default InstantReport;
